import { ScaleLinear } from "d3";
import React from "react";

import {
  segmentCount,
  segmentDegrees,
  segmentShift,
} from "../../chartGeometry";
import { ConfigData } from "../../config";
import { hashFraction } from "../../hash";
import { Blip, FlagType, Item, Point } from "../../model";
import Link from "../Link/Link";
import { ChangedBlip, DefaultBlip, NewBlip } from "./BlipShapes";

/*
See https://medium.com/create-code/build-a-radar-diagram-with-d3-js-9db6458a9248
for a good explanation of formulas used to calculate various things in this component
*/

/** Max number of deterministic placements tried before giving up on a blip. */
const MAX_PLACEMENT_ATTEMPTS = 100;

/**
 * Fraction used for a given placement attempt.
 *
 * Attempt 0 uses the fraction baked into `rd.json` by the generator, so a blip
 * that does not collide always lands on the exact same pixel. Later attempts
 * are *also* deterministic but indexed on the attempt number: without that
 * index the collision loop would regenerate the same point 100 times and give
 * up, and blips would end up stacked on top of each other.
 */
function attemptFraction(
  name: string,
  kind: "angle" | "radius",
  baked: number | undefined,
  attempt: number
): number {
  if (attempt === 0) {
    return baked ?? hashFraction(`${name}:${kind}:0`);
  }
  return hashFraction(`${name}:${kind}:${attempt}`);
}

function generateCoordinates(
  blip: Blip,
  xScale: ScaleLinear<number, number>,
  yScale: ScaleLinear<number, number>,
  config: ConfigData,
  attempt: number
): Point {
  const pi = Math.PI,
    segments = segmentCount(config),
    ringRadius = config.chartConfig.ringsAttributes[blip.ringPosition].radius,
    previousRingRadius =
      blip.ringPosition === 0
        ? 0
        : config.chartConfig.ringsAttributes[blip.ringPosition - 1].radius,
    ringPadding = 0.7;

  // radian inside the segment's angular width (90 degrees when N = 4),
  // kept away from the segment borders by 10% on each side
  const randomDegree =
    ((0.1 +
      attemptFraction(blip.name, "angle", blip.angleFraction, attempt) * 0.8) *
      segmentDegrees(segments) *
      pi) /
    180;
  // distance from the centre of the radar, but within given ring. Also, with some "padding" so the points don't touch ring borders.
  const radius = pointBetween(
    previousRingRadius + ringPadding,
    ringRadius - ringPadding,
    attemptFraction(blip.name, "radius", blip.radiusFraction, attempt)
  );

  // Rotation that moves the in-segment angle onto the blip's own segment.
  const shift = segmentShift(blip.quadrantPosition, segments);

  return {
    x: xScale(Math.cos(randomDegree + shift) * radius),
    y: yScale(Math.sin(randomDegree + shift) * radius),
  };
}

function pointBetween(min: number, max: number, amount: number): number {
  return amount * (max - min) + min;
}

function distanceBetween(point1: Point, point2: Point): number {
  const a = point2.x - point1.x;
  const b = point2.y - point1.y;
  return Math.sqrt(a * a + b * b);
}

function renderBlip(
  blip: Blip,
  index: number,
  config: ConfigData
): JSX.Element {
  const props = {
    blip,
    className: "blip",
    fill: blip.colour,
    "data-background-color": blip.colour,
    "data-text-color": blip.txtColour,
    "data-tip": blip.title,
    key: index,
  };
  switch (blip.flag) {
    case FlagType.new:
      return <NewBlip {...props} config={config} />;
    case FlagType.changed:
      return <ChangedBlip {...props} config={config} />;
    default:
      return <DefaultBlip {...props} config={config} />;
  }
}

const BlipPoints: React.FC<{
  items: Item[];
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  config: ConfigData;
}> = ({ items, xScale, yScale, config }) => {
  const blips: Blip[] = items.reduce((list: Blip[], item: Item) => {
    if (!item.ring || !item.quadrant) {
      // skip the blip if it doesn't have a ring or quadrant assigned
      return list;
    }
    const quadrantConfig = config.quadrantsMap[item.quadrant];
    if (!quadrantConfig) {
      return list;
    }

    let blip: Blip = {
      ...item,
      quadrantPosition: quadrantConfig.position,
      ringPosition: config.rings.findIndex((r) => r === item.ring),
      colour: quadrantConfig.colour,
      txtColour: quadrantConfig.txtColour,
      coordinates: { x: 0, y: 0 },
    };

    /*
      Try deterministic placements until one has a satisfactory distance to
      every already placed blip (so that they don't touch each other) and to
      the quadrant borders (so that they don't overlap quadrants).
      Since `createItems` sorts items by name, the placement order - and
      therefore the whole layout - is reproducible for a given
      (radar, release, filter) triplet.
    */
    let point: Point = generateCoordinates(blip, xScale, yScale, config, 0);
    for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
      point = generateCoordinates(blip, xScale, yScale, config, attempt);

      const touchesAxis =
        Math.abs(point.x - xScale(0)) < 15 ||
        Math.abs(point.y - yScale(0)) < 15;
      const touchesOtherBlip = list.some(
        (b) =>
          distanceBetween(point, b.coordinates) <
          config.chartConfig.blipSize + config.chartConfig.blipSize / 2
      );

      if (!touchesAxis && !touchesOtherBlip) {
        break;
      }
    }

    blip.coordinates = point;

    list.push(blip);
    return list;
  }, []);

  return (
    <g className="blips">
      {blips.map((blip, index) => (
        <Link pageName={`${blip.quadrant}/${blip.name}`} key={index}>
          {renderBlip(blip, index, config)}
        </Link>
      ))}
    </g>
  );
};

export default BlipPoints;
